
import { useMantineColorScheme } from "@mantine/core";
import * as d3 from 'd3';
import { useEffect, useRef } from "react";
import {
  formatValueByChartTitle,
  formatTooltipValueByChartTitle,
} from "../../../utils/helpers";

const BarChartWithCurvedEdges = ({ title = "", categories = [], data = [], barColors = [] }) => {
  const { colorScheme } = useMantineColorScheme();
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const tooltipRef = useRef(null);
  const resizeObserverRef = useRef(null);

  const blueShades = ["#076692", "#0F81B3", "#42B4E6", "#0187CE"];
  const fontFamily = 'Nunito, sans-serif';

  const formatValueByTitle = (value) => formatValueByChartTitle(title, value);
  const formatTooltipValueByTitle = (value) =>
    formatTooltipValueByChartTitle(title, value);

  const measureTextWidth = (text, font) => {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return 0;
      ctx.font = font;
      return ctx.measureText(String(text ?? '')).width || 0;
    } catch {
      return 0;
    }
  };

  useEffect(() => {
    if (!containerRef.current) return;

    // Create tooltip div once
    if (!tooltipRef.current) {
      const div = document.createElement('div');
      div.style.position = 'absolute';
      div.style.pointerEvents = 'none';
      div.style.background = colorScheme === 'dark' ? '#222' : '#ffffff';
      div.style.border = '1px solid ' + (colorScheme === 'dark' ? '#555' : '#888');
      div.style.padding = '4px 6px';
      div.style.fontSize = '12px';
      div.style.fontFamily = fontFamily;
      div.style.borderRadius = '4px';
      div.style.boxShadow = '0 2px 6px rgba(0,0,0,0.25)';
      div.style.zIndex = 10;
      div.style.display = 'none';
      tooltipRef.current = div;
      containerRef.current.style.position = 'relative';
      containerRef.current.appendChild(div);
    }

    function draw(){
      const el = containerRef.current;
      const width = Math.max(0, el.clientWidth - 32); // account for internal padding
      const height = Math.max(0, el.clientHeight - 32); // account for internal padding
      if (width === 0 || height === 0) return;
      // Prepare category/data arrays and estimate label wrapping to avoid cutting long category labels
      const safeCategories = categories || [];
      const numericData = data || [];
      const approxCharsPerLine = 10;
      const maxLabelLines = d3.max(safeCategories.map(d => {
        const txt = (d || '').toString();
        return Math.max(1, Math.ceil(txt.length / approxCharsPerLine));
      })) || 1;
      const margin = { top: 36, right: 8, bottom: 24 + (maxLabelLines - 1) * 12, left: 40 };
      const innerH = Math.max(0, height - margin.top - margin.bottom);

      // Clear previous svg
      if (svgRef.current) {
        svgRef.current.remove();
      }
      const svg = d3.select(el).append('svg')
        .attr('width', width)
        .attr('height', height)
        .style('font-family', fontFamily);
      svgRef.current = svg.node();

      // Title (centered horizontally within the SVG)
      svg.append('text')
        .attr('x', Math.round(width / 2))
        .attr('y', 16)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .text(title)
        .attr('fill', colorScheme === 'dark' ? '#d5d7e0' : '#495057')
        .attr('font-size', 14)
        .attr('font-weight', 600)
        .attr('font-family', fontFamily);

      const values = (numericData || []).filter(v => typeof v === 'number' && !Number.isNaN(v));
      const rawMin = d3.min(values);
      const rawMax = d3.max(values);
      const hasNegative = values.some(v => v < 0);
      const minVal = hasNegative ? (rawMin ?? 0) : 0;
      // Keep a zero baseline whenever negatives exist.
      const maxVal = hasNegative ? Math.max(0, rawMax ?? 0) : (rawMax ?? 0);

      let paddedMin;
      let paddedMax;

      if (hasNegative) {
        const span = (maxVal - minVal) || 1;
        paddedMin = minVal - span * 0.1;
        paddedMax = maxVal + span * 0.1;
      } else {
        // All values are >= 0: don't pad below zero.
        const safeMax = Math.max(0, maxVal);
        paddedMin = 0;
        paddedMax = safeMax === 0 ? 1 : safeMax * 1.1;
      }

      // Ensure y-axis labels never get clipped by dynamically sizing left margin.
      const tickFont = `600 10px ${fontFamily}`;
      const tickScale = d3.scaleLinear().domain([paddedMin, paddedMax]).nice(3);
      const tickLabels = tickScale.ticks(3).map(formatValueByTitle);
      const maxTickWidth = d3.max(tickLabels.map(t => measureTextWidth(t, tickFont))) || 0;
      const yAxisLabelPadding = 18; // accounts for tick padding + axis offset
      margin.left = Math.max(margin.left, Math.ceil(maxTickWidth + yAxisLabelPadding));

      // Move g tag to the right (requested) without risking label clipping.
      const plotOffsetX = 10;
      const plotTranslateX = margin.left + plotOffsetX;

      const innerW = Math.max(0, width - plotTranslateX - margin.right);

      const g = svg.append('g').attr('transform', `translate(${plotTranslateX},${margin.top})`);

      const y = d3.scaleLinear().domain(tickScale.domain()).range([innerH, 0]);
      const x = d3.scaleBand().domain(safeCategories).range([0, innerW]).padding(0.35);

      // Axes
      const xAxis = d3.axisBottom(x).tickFormat(d => (d == null ? '' : d));
      const yAxis = d3.axisLeft(y).ticks(3).tickFormat(formatValueByTitle);

      const xAxisG = g.append('g').attr('transform', `translate(0,${innerH})`).call(xAxis);
      xAxisG.selectAll('text')
        .attr('font-size', 10)
        .attr('font-weight', 600)
        .attr('fill', colorScheme === 'dark' ? '#d5d7e0' : '#495057')
        .attr('font-family', fontFamily)
        .each(function() {
          const self = d3.select(this);
          const fullText = self.text() || '';
          // simple word-wrap into lines approximating characters per line
          const words = fullText.split(/\s+/).filter(Boolean);
          const lines = [];
          let current = '';
          words.forEach((w) => {
            if ((current + ' ' + w).trim().length <= approxCharsPerLine) {
              current = (current + ' ' + w).trim();
            } else {
              if (current) lines.push(current);
              current = w;
            }
          });
          if (current) lines.push(current || '');

          if (lines.length > 1) {
            self.text(null);
            lines.forEach((ln, i) => {
              self.append('tspan')
                .attr('x', 0)
                .attr('dy', i === 0 ? 6 : 12)
                .text(ln);
            });
          }
        });
      xAxisG.selectAll('line, path').attr('stroke','none');

      const yAxisG = g.append('g').call(yAxis);
      yAxisG.selectAll('text')
        .attr('font-size', 10)
        .attr('font-weight', 600)
        .attr('fill', colorScheme === 'dark' ? '#d5d7e0' : '#495057')
        .attr('font-family', fontFamily);
      yAxisG.selectAll('line, path').attr('stroke','none');

      // Zero baseline (so negative values are visually obvious)
      const yZero = y(0);
      g.append('line')
        .attr('x1', 0)
        .attr('x2', innerW)
        .attr('y1', yZero)
        .attr('y2', yZero)
        .attr('stroke', colorScheme === 'dark' ? '#555' : '#888')
        .attr('stroke-width', 1)
        .attr('shape-rendering', 'crispEdges');

      // Bars
      const barWidth = Math.min(15, x.bandwidth());
      const tooltip = tooltipRef.current;

      const bars = g.selectAll('.bar').data(numericData.map((v,i)=> ({
        value: v,
        category: safeCategories[i] || '',
        color: barColors?.[i] || blueShades[numericData.length === 2 ? i + 2 : i % blueShades.length]
      })), d=> d.category);

      const barGroup = bars.enter().append('g').attr('class','bar');

      barGroup.append('rect')
        .attr('x', d => (x(d.category) ?? 0) + (x.bandwidth() - barWidth)/2 )
        .attr('y', yZero)
        .attr('width', barWidth)
        .attr('height', 0)
        // sharp edges (no rounded corners)
        .attr('fill', d=> d.color)
        .style('cursor','pointer')
        .on('mouseenter', function(e,d){
          tooltip.style.display='block';
          tooltip.innerHTML = `${d.category}: <b>${formatTooltipValueByTitle(d?.value)}</b>`;
        })
        .on('mousemove', function(e){
          const bounds = el.getBoundingClientRect();
          tooltip.style.left = (e.clientX - bounds.left + 8)+'px';
          tooltip.style.top = (e.clientY - bounds.top - 28)+'px';
        })
        .on('mouseleave', function(){
          tooltip.style.display='none';
        })
        .transition()
        .duration(550)
        .attr('y', d => (d.value >= 0 ? y(d.value) : yZero))
        .attr('height', d => Math.abs(y(d.value) - yZero));

      // Value labels
      const valueLabels = barGroup.append('text')
        .attr('x', d => (x(d.category) ?? 0) + x.bandwidth()/2)
        .attr('y', d => {
          const yVal = y(d.value);
          const desired = d.value >= 0 ? (yVal - 8) : (yVal + 12);
          const topOutside = -6;
          const bottomOutside = innerH + 14;
          return d.value >= 0
            ? Math.max(topOutside, desired)
            : Math.min(bottomOutside, desired);
        })
        .attr('text-anchor','middle')
        .attr('font-size',12)
        .attr('font-family', fontFamily)
        .attr('fill', colorScheme === 'dark' ? '#d5d7e0' : '#495057')
        .attr('opacity',0)
        .text(d => formatValueByTitle(d?.value));
      valueLabels.append('title')
        .text(d => `${d?.category}: ${formatTooltipValueByTitle(d?.value)}`);
      valueLabels.transition().delay(300).duration(400).attr('opacity',1);
    }

    draw();

    if (!resizeObserverRef.current && containerRef.current) {
      resizeObserverRef.current = new ResizeObserver(() => {
        if (containerRef.current) draw();
      });
      resizeObserverRef.current.observe(containerRef.current);
    }
    return () => {
      if (resizeObserverRef.current) {
        if (containerRef.current) {
          resizeObserverRef.current.unobserve(containerRef.current);
        }
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
    };
  }, [title, categories, data, colorScheme]);

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        minWidth: 0,
        height: 200,
        backgroundColor: colorScheme === 'dark' ? '#1c1e20' : '#f9f9fa',
        padding: 16,
        boxSizing: 'border-box',
        fontFamily: 'Nunito, sans-serif'
      }}
    />
  );
};

export default BarChartWithCurvedEdges;
